// Fund investor-statements sub-route — /fund/:fundId/statements (16.e.3.5b.1).
// The manager surface for the 16.e.3.5a endpoints: issue one immutable statement
// per current roster investor for a chosen period, then see the fund's issued
// statements with a PDF download per row. Self-loads via GET
// /ops/funds/{id}/investor-statements; issues via POST (same path), surfacing the
// per-investor issued/skipped/roster counts the endpoint returns so a silent
// total-wipeout batch is visible. A per-row "Correct" action supersedes a live
// statement via POST /ops/fund-investor-statements/{id}/supersede (CR.a, wiring
// the 16.e.3.6 endpoint to the UI). Manager-gated in App.tsx (under FundLayout).
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader, Pill } from '../../components/shell';
import { api } from '../../lib/api';

interface FundStatementRow {
  id: string;
  investor_id: string;
  investor_name: string;
  period_label: string;
  period_start: string;
  period_end: string;
  version: number;
  issued_at: string | null;
  status_label: 'issued' | 'correction' | 'superseded';
  superseded: boolean;
  superseded_by_id: string | null;
  base_currency: string;
  pdf_available: boolean;
  note: string;
}

interface IssueResult {
  period_label: string;
  roster_count: number;
  issued_count: number;
  skipped_count: number;
  issued: { id: string; investor_id: string; investor_name: string; version: number }[];
  skipped: { investor_id: string; investor_name: string }[];
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

// Mirrors the SMA StatementViewer reason set (Statement.SUPERSESSION_REASON_CHOICES).
const SUPERSEDE_REASONS: [string, string][] = [
  ['data_correction', 'Data correction'],
  ['pricing_correction', 'Pricing correction'],
  ['fee_adjustment', 'Fee adjustment'],
  ['restatement', 'Restatement'],
  ['other', 'Other'],
];

export default function FundStatements() {
  const { fundId } = useParams<{ fundId: string }>();
  const [rows, setRows] = useState<FundStatementRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Issue form state.
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [result, setResult] = useState<IssueResult | null>(null);

  // Correction (supersede) state — CR.a. Wires the 16.e.3.6 endpoint to the UI:
  // a manager corrects one live statement → v+1, the original superseded.
  const [correctingId, setCorrectingId] = useState<string | null>(null);
  const [correctReason, setCorrectReason] = useState('data_correction');
  const [correctNote, setCorrectNote] = useState('');
  const [correctBusy, setCorrectBusy] = useState(false);
  const [correctErr, setCorrectErr] = useState<string | null>(null);

  useEffect(() => {
    if (!fundId) return;
    let cancelled = false;
    setLoadErr(null);
    api<FundStatementRow[]>(`/ops/funds/${fundId}/investor-statements`)
      .then(r => { if (!cancelled) setRows(r); })
      .catch(() => { if (!cancelled) setLoadErr('Failed to load statements.'); });
    return () => { cancelled = true; };
  }, [fundId, reloadKey]);

  const issue = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !fundId) return;
    setFormErr(null); setResult(null);
    if (!periodStart || !periodEnd) { setFormErr('Choose a period start and end.'); return; }
    if (periodStart > periodEnd) { setFormErr('Period start must be on or before the end.'); return; }
    setBusy(true);
    try {
      const res = await api<IssueResult>(`/ops/funds/${fundId}/investor-statements`, {
        method: 'POST',
        body: { period_start: periodStart, period_end: periodEnd, label: label.trim() || undefined },
      });
      setResult(res);
      setReloadKey(k => k + 1);
    } catch (err: any) {
      setFormErr(err?.payload?.detail || 'Could not issue statements. Check the period and try again.');
    } finally { setBusy(false); }
  };

  const openCorrection = (id: string) => {
    setCorrectingId(id); setCorrectReason('data_correction');
    setCorrectNote(''); setCorrectErr(null);
  };

  const submitCorrection = async () => {
    if (correctBusy || !correctingId) return;
    setCorrectBusy(true); setCorrectErr(null);
    try {
      await api(`/ops/fund-investor-statements/${correctingId}/supersede`, {
        method: 'POST',
        body: { reason: correctReason, note: correctNote.trim() || undefined },
      });
      setCorrectingId(null); setCorrectNote('');
      setReloadKey(k => k + 1);
    } catch (err: any) {
      setCorrectErr(err?.payload?.detail || 'Could not issue the correction.');
    } finally { setCorrectBusy(false); }
  };

  if (!fundId) return null;

  const liveCount = (rows || []).filter(r => !r.superseded).length;

  return (
    <>
      <PageHeader
        kicker="FUND"
        title={<>Investor <em>statements.</em></>}
        meta={rows ? (
          <>
            <span className="label-s">{liveCount} LIVE · {rows.length} TOTAL</span>
            <span className="label-s">IMMUTABLE ONCE ISSUED</span>
          </>
        ) : undefined}
      />
      {loadErr && <p className="serif-body" style={{ color: 'var(--oxblood)', marginTop: 12 }}>{loadErr}</p>}

      {/* ---- Issue form ---- */}
      <section style={{ marginTop: 24 }}>
        <span className="kicker">ISSUE STATEMENTS FOR A PERIOD</span>
        <p className="serif-body" style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--ink-2)', margin: '8px 0 16px', maxWidth: '64ch' }}>
          Issues one statement for every current investor, valued at the period-end NAV strike. Re-issuing the same period supersedes the prior version — it never replaces it.
        </p>
        <form onSubmit={issue} style={{ borderTop: '1px solid var(--ink)', paddingTop: 18,
                                        display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label className="field-label">PERIOD START</label>
              <input className="field-input" type="date" required value={periodStart}
                     onChange={e => setPeriodStart(e.target.value)} />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label className="field-label">PERIOD END</label>
              <input className="field-input" type="date" required value={periodEnd}
                     onChange={e => setPeriodEnd(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label className="field-label">LABEL (OPTIONAL)</label>
            <input className="field-input" value={label} onChange={e => setLabel(e.target.value)}
                   placeholder="May 2026" />
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6,
                          fontFamily: 'var(--serif)', fontStyle: 'italic' }}>
              Defaults to the month of the period end.
            </div>
          </div>
          {formErr && (
            <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14 }}>
              {formErr}
            </div>
          )}
          <div>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Issuing…' : <>Issue statements <span className="btn-arrow">→</span></>}
            </button>
          </div>
        </form>

        {/* ---- Result panel ---- */}
        {result && (
          <div style={{ marginTop: 20, borderTop: 'var(--hairline)', paddingTop: 16 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="label-s">{result.period_label}</span>
              <Pill tone="accent">{result.issued_count} ISSUED</Pill>
              {result.skipped_count > 0 && <Pill tone="warn">{result.skipped_count} SKIPPED</Pill>}
              <span className="label-s" style={{ color: 'var(--ink-3)' }}>{result.roster_count} ON ROSTER</span>
            </div>
            {result.issued_count === 0 && (
              <p className="serif-body" style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--oxblood)', marginTop: 12 }}>
                No statements were issued for this period. Check that a NAV strike exists at the period end and that the fund has investors.
              </p>
            )}
            {result.skipped.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <span className="label-s" style={{ color: 'var(--ink-3)' }}>SKIPPED INVESTORS</span>
                <div className="serif-body" style={{ fontSize: 14, color: 'var(--ink-2)', marginTop: 4 }}>
                  {result.skipped.map(s => s.investor_name).join(', ')}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ---- Issued statements list ---- */}
      <section style={{ marginTop: 48 }}>
        <span className="kicker">ISSUED STATEMENTS</span>
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>INVESTOR</th><th>PERIOD</th><th data-align="right">VERSION</th>
              <th data-align="right">ISSUED</th><th data-align="right">PDF</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map(r => (
              <tr key={r.id} style={{ opacity: r.superseded ? 0.5 : 1 }}>
                <td>{r.investor_name}</td>
                <td>{r.period_label}</td>
                <td data-align="right">
                  <span className="num">v{r.version}</span>
                  {r.status_label === 'correction' && <> <Pill tone="accent">CORRECTION</Pill></>}
                  {r.superseded && <> <Pill tone="warn">SUPERSEDED</Pill></>}
                </td>
                <td data-align="right" className="num">{fmtDate(r.issued_at)}</td>
                <td data-align="right">
                  <span style={{ display: 'inline-flex', gap: 16, alignItems: 'baseline' }}>
                    {r.pdf_available ? (
                      <a className="btn-link" href={`/api/v1/fund-investor-statements/${r.id}/pdf`}
                         download style={{ textDecoration: 'none' }}>Download ↓</a>
                    ) : (
                      <span className="label-s" style={{ color: 'var(--ink-3)' }}>RENDERING…</span>
                    )}
                    {!r.superseded && (
                      <button type="button" className="btn-link" onClick={() => openCorrection(r.id)}>
                        Correct
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
            {rows && !rows.length && (
              <tr><td colSpan={5} className="label-s" style={{ color: 'var(--ink-3)' }}>
                No statements issued yet. Issue the first period above.
              </td></tr>
            )}
          </tbody>
        </table>

        {/* ---- Correction (supersede) panel — CR.a ---- */}
        {correctingId && (() => {
          const tgt = (rows || []).find(r => r.id === correctingId);
          return (
            <div style={{ marginTop: 24, borderTop: '1px solid var(--ink)', paddingTop: 18, maxWidth: 560 }}>
              <span className="kicker">ISSUE A CORRECTION</span>
              <p className="serif-body" style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--ink-2)', margin: '8px 0 16px' }}>
                Recomputes {tgt ? `${tgt.investor_name}'s ${tgt.period_label}` : 'the'} statement from current
                data and issues v{(tgt?.version ?? 1) + 1}, superseding — but not replacing — v{tgt?.version ?? 1}.
                The original stays on record.
              </p>
              <div className="field">
                <label className="field-label">REASON</label>
                <select className="field-input" value={correctReason}
                        onChange={e => setCorrectReason(e.target.value)}>
                  {SUPERSEDE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label className="field-label">NOTE (OPTIONAL)</label>
                <input className="field-input" value={correctNote}
                       onChange={e => setCorrectNote(e.target.value)} placeholder="Why this correction" />
              </div>
              {correctErr && (
                <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14, marginTop: 12 }}>
                  {correctErr}
                </div>
              )}
              <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginTop: 16 }}>
                <button type="button" className="btn" disabled={correctBusy} onClick={submitCorrection}>
                  {correctBusy ? 'Issuing…' : <>Issue correction <span className="btn-arrow">→</span></>}
                </button>
                <button type="button" className="btn-link" onClick={() => setCorrectingId(null)}>Cancel</button>
              </div>
            </div>
          );
        })()}
      </section>
    </>
  );
}
